import {useCallback, useState, useMemo} from 'react'
import {RepositoryAndIssuePicker, type PickerType} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {graphql, useLazyLoadQuery, useRelayEnvironment} from 'react-relay'
import {addBlockedByMutation} from '../../../mutations/add-blocked-by-mutation'
import {removeBlockedByMutation} from '../../../mutations/remove-blocked-by-mutation'
import type {DependenciesPickerBlockedByIssuesQuery} from './__generated__/DependenciesPickerBlockedByIssuesQuery.graphql'

type DependenciesPickerProps = {
  type: 'blockedBy'
  onClose?: () => void
  issueId: string
  organization: string
  defaultRepositoryNameWithOwner: string
  anchorRef: React.RefObject<HTMLElement>
}

export const DependenciesPickerBlockedByIssuesGraphqlQuery = graphql`
  query DependenciesPickerBlockedByIssuesQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        id
        blockedBy(first: 100) {
          nodes {
            id
          }
        }
      }
    }
  }
`

export function DependenciesPicker({
  type,
  onClose,
  issueId,
  organization,
  defaultRepositoryNameWithOwner,
  anchorRef,
}: DependenciesPickerProps) {
  const environment = useRelayEnvironment()
  const [pickerType, setPickerType] = useState<PickerType>('Issue')

  // Query to get the issue data
  const queryData = useLazyLoadQuery<DependenciesPickerBlockedByIssuesQuery>(
    DependenciesPickerBlockedByIssuesGraphqlQuery,
    {id: issueId},
    // TODO: we should use the graphql store as well, but we need to invalidate the cache after mutations complete
    {fetchPolicy: 'network-only'},
  )

  // Process the fetched blockedBy issues into the format expected by the picker
  const blockedByIssueIds = useMemo(() => {
    if (!queryData.node?.blockedBy?.nodes) return []

    return queryData.node.blockedBy.nodes.filter(issue => !!issue).map(issue => issue.id)
  }, [queryData])

  const onPickerTypeChange = useCallback(
    (t: PickerType) => {
      setPickerType(t)
      if (t === null && onClose) {
        onClose()
      }
    },
    [onClose],
  )

  const onIssueSelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      if (type === 'blockedBy') {
        // Compute the set difference between the currently selected issue IDs and the newly selected issue IDs
        const selectedIssueIds = new Set(selectedIssues.map(issue => issue.id))
        const blockedByIssueIdsSet = new Set(blockedByIssueIds)
        const issuesToAdd = Array.from(selectedIssueIds).filter(id => !blockedByIssueIdsSet.has(id))
        const issuesToRemove = Array.from(blockedByIssueIdsSet).filter(id => !selectedIssueIds.has(id))

        for (const blockingIssueId of issuesToAdd) {
          // Call the mutation to add the blockedBy relationship
          addBlockedByMutation({
            environment,
            input: {
              issueId,
              blockingIssueId,
            },
            onError: () => {
              // TODO: Implement graceful error handling.
            },
          })
        }

        for (const blockingIssueId of issuesToRemove) {
          // Call the mutation to remove the blockedBy relationship
          removeBlockedByMutation({
            environment,
            input: {
              issueId,
              blockingIssueId,
            },
            onError: () => {
              // TODO: Implement graceful error handling.
            },
          })
        }
      }
    },
    [environment, issueId, blockedByIssueIds, type],
  )

  return (
    <RepositoryAndIssuePicker
      anchorElement={props => {
        const {ref} = props as {
          ref: React.MutableRefObject<HTMLElement | null>
        }
        if (ref) {
          ref.current = anchorRef.current
        }
        return <></>
      }}
      onIssueSelection={onIssueSelection}
      organization={organization}
      defaultRepositoryNameWithOwner={defaultRepositoryNameWithOwner}
      pickerType={pickerType}
      onPickerTypeChange={onPickerTypeChange}
      selectionVariant="multiple"
      selectedIssueIds={blockedByIssueIds}
      hiddenIssueIds={[issueId]}
    />
  )
}

try{ DependenciesPicker.displayName ||= 'DependenciesPicker' } catch {}